import { Patient } from '@models/customer-care';

import { mockStore } from './mock-store';

const delay = (ms = 300) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export interface PublicFeedbackContext {
  org: { name: string };
  /** Present only when a valid patientId belonging to THIS org is supplied. */
  patient: Pick<Patient, 'id' | 'firstName' | 'lastName' | 'phoneNumber'> | null;
}

export interface SubmitFeedbackInput {
  patientId?: string | null;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  rating: number; // 1..10
  notes?: string;
  marketingConsent: boolean;
  twilioOptIn: boolean;
}

/**
 * Public feedback API. Everything is keyed off an opaque per-org `publicToken` so a
 * bad actor cannot enumerate other businesses by incrementing a sequential id.
 * Unknown tokens resolve to null (caller shows a generic "link not found").
 */
const feedbackApi = {
  /**
   * Fast patient lookup by id across all org buckets.
   * Returns the patient + org name without requiring token resolution.
   */
  lookupPatient: (patientId: string): { patient: PublicFeedbackContext['patient']; orgName: string | null } => {
    const store = mockStore.readStore();
    for (const bucket of Object.values(store.byOrg)) {
      const found = bucket.patients.find((p) => p.id === patientId);
      if (found) {
        const org = store.orgs[found.organizationId];
        return {
          patient: {
            id: found.id,
            firstName: found.firstName,
            lastName: found.lastName,
            phoneNumber: found.phoneNumber,
          },
          orgName: org?.name ?? null,
        };
      }
    }
    return { patient: null, orgName: null };
  },

  submitFeedback: async (token: string, input: SubmitFeedbackInput): Promise<{ ok: true }> => {
    await delay();
    const org = mockStore.getOrgByPublicToken(token);
    if (!org) throw new Error('Invalid link');

    const store = mockStore.readStore();
    const bucket = store.byOrg[org.organizationId] ?? mockStore.getBucket(store, org.organizationId);
    const ts = mockStore.nowIso();

    // Find existing patient (must belong to this org) or create a new one bound to the org.
    const existing = input.patientId
      ? bucket.patients.find((p) => p.id === input.patientId && p.organizationId === org.organizationId)
      : undefined;

    let patient: Patient;
    if (!existing) {
      patient = {
        id: mockStore.uuid(),
        organizationId: org.organizationId, // forced to the resolved org — never client-supplied
        phoneNumber: input.phoneNumber.trim(),
        firstName: input.firstName.trim(),
        lastName: input.lastName.trim(),
        email: null,
        pronouns: null,
        marketingConsent: input.marketingConsent,
        messagingConsent: input.twilioOptIn,
        createdAt: ts,
        updatedAt: ts,
      };
      bucket.patients.push(patient);
      bucket.chatEvents.push({
        id: mockStore.uuid(),
        patientId: patient.id,
        eventType: 'PATIENT_ADDED',
        payload: JSON.stringify({ title: `${patient.firstName} ${patient.lastName} self-registered` }),
        sourceType: undefined,
        sourceId: undefined,
        occurredAt: ts,
        createdAt: ts,
        updatedAt: ts,
      });
    } else {
      patient = existing;
      patient.marketingConsent = input.marketingConsent;
      patient.messagingConsent = input.twilioOptIn;
      patient.updatedAt = ts;
    }

    // Record feedback against the patient's most recent treatment (or a standalone record).
    const treatment = bucket.patientTreatments
      .filter((t) => t.patientId === patient.id)
      .sort((a, b) => (a.appliedAt < b.appliedAt ? 1 : -1))[0];

    if (treatment) {
      treatment.feedbackRating = input.rating;
      treatment.feedbackNotes = input.notes ?? null;
      treatment.feedbackAt = ts;
    }

    bucket.chatEvents.push({
      id: mockStore.uuid(),
      patientId: patient.id,
      eventType: 'FEEDBACK',
      payload: JSON.stringify({
        title: `Feedback received: ${input.rating}/10`,
        rating: input.rating,
        text: input.notes ?? '',
        twilioOptIn: input.twilioOptIn,
      }),
      sourceType: treatment ? 'TREATMENT' : undefined,
      sourceId: treatment?.id,
      occurredAt: ts,
      createdAt: ts,
      updatedAt: ts,
    });

    store.byOrg[org.organizationId] = bucket;
    mockStore.writeStore(store);
    return { ok: true };
  },
};

export default feedbackApi;
